/**
 * Claims Management Page
 * Healthcare claims listing, tracking, and management
 * Single and bulk claim entry are now separate pages
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { getWeaveinApiUrl } from '@/src/config';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Send,
  Trash2,
  AlertCircle,
  DollarSign,
  Calendar,
  User,
  Building2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Table2
} from 'lucide-react';

import type { Claim, Payer } from '@/services/api/claims';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { ClaimReadinessPanel } from '@/components/billing/ClaimReadinessPanel';
import { formatDateOnly } from '@/utils/dateTime';

interface ClaimsPageProps {
  onNavigate?: (tab: string) => void;
}

interface Member {
  id: number;
  first_name: string;
  last_name: string;
  member_id: string;
}

const CLAIM_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'gray' },
  { value: 'validated', label: 'Validated', color: 'blue' },
  { value: 'submitted', label: 'Submitted', color: 'yellow' },
  { value: 'accepted', label: 'Accepted', color: 'green' },
  { value: 'rejected', label: 'Rejected', color: 'red' },
  { value: 'denied', label: 'Denied', color: 'red' },
  { value: 'partial_paid', label: 'Partial Paid', color: 'orange' },
  { value: 'paid', label: 'Paid', color: 'green' },
  { value: 'appealed', label: 'Appealed', color: 'purple' }
];

export default function ClaimsPage({ onNavigate }: ClaimsPageProps) {
  const { t } = useAppTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [claims, setClaims] = useState<Claim[]>([]);
  const [payers, setPayers] = useState<Payer[]>([]);
  const [members, setMembers] = useState<Member[]>([]);

  // UI State
  const [expandedClaimId, setExpandedClaimId] = useState<number | null>(null);
  const [expandedClaimDetail, setExpandedClaimDetail] = useState<any>(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPayerId, setFilterPayerId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load initial data
  useEffect(() => {
    Promise.all([
      fetchClaims(),
      fetchPayers(),
      fetchMembers()
    ]);
  }, []);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId })
    };
  };

  // Fetch functions
  const fetchClaims = async () => {
    setLoading(true);
    try {
      const response = await fetch(getWeaveinApiUrl('/api/claims'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setClaims(data);
      } else if (response.status === 401) {
        setError('Authentication required. Please log in again.');
      }
    } catch (err) {
      console.error('Error fetching claims:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayers = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/claims/payers'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPayers(data);
      }
    } catch (err) {
      console.error('Error fetching payers:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/members?page_size=1000'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const membersList = data.members || data || [];
        const mappedMembers = membersList.map((m: any) => ({
          ...m,
          member_id: m.member_number || m.member_id || m.id
        }));
        setMembers(mappedMembers);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  // CRUD operations
  const handleSubmitClaim = async (claimId: number) => {
    if (!window.confirm('Are you sure you want to submit this claim? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/claims/${claimId}/submit`), {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchClaims();
      } else {
        const errorData = await response.json();
        // TRUS-722: a held claim answers 409 with {message, reasons}.
        const detail = errorData.detail;
        setError(
          (typeof detail === 'string' && detail) ||
            (detail && typeof detail.message === 'string' && detail.message) ||
            'Failed to submit claim',
        );
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit claim');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClaim = async (claimId: number, claimNumber: string) => {
    if (!window.confirm(`Are you sure you want to delete claim ${claimNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(getWeaveinApiUrl(`/api/claims/${claimId}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchClaims();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete claim');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete claim');
    }
  };

  const handleExpandClaim = async (claimId: number, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedClaimId(null);
      setExpandedClaimDetail(null);
      return;
    }
    setExpandedClaimId(claimId);
    setExpandedClaimDetail(null);
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/claims/${claimId}`), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setExpandedClaimDetail(data);
      }
    } catch (err) {
      console.error('Error fetching claim detail:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = CLAIM_STATUSES.find(s => s.value === status);
    if (!statusConfig) return <Badge variant="outline">{status}</Badge>;

    const colorClasses = {
      gray: 'bg-gray-100 text-gray-800',
      blue: 'bg-blue-100 text-blue-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      orange: 'bg-orange-100 text-orange-800',
      purple: 'bg-purple-100 text-purple-800'
    };

    return (
      <Badge className={colorClasses[statusConfig.color as keyof typeof colorClasses]}>
        {statusConfig.label}
      </Badge>
    );
  };

  // Filter claims
  const filteredClaims = claims.filter(claim => {
    if (filterStatus && claim.claim_status !== filterStatus) return false;
    if (filterPayerId && claim.payer_id !== filterPayerId) return false;
    if (searchTerm && !claim.claim_number.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Error Display */}
      {error && (
        <Card className="p-4 mb-6 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
              className="ml-auto"
            >
              {t('claims.dismiss', 'Dismiss')}
            </Button>
          </div>
        </Card>
      )}

      {/* Toolbar */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              className="p-2 border rounded-md"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="">{t('claims.all_statuses', 'All Statuses')}</option>
              {CLAIM_STATUSES.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <select
              className="p-2 border rounded-md"
              value={filterPayerId || ''}
              onChange={e => setFilterPayerId(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">{t('claims.all_payers', 'All Payers')}</option>
              {payers.map(payer => (
                <option key={payer.id} value={payer.id}>
                  {payer.payer_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 flex-1">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder={t('claims.search_by_claim_number', 'Search by claim number...')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="max-w-xs"
            />
          </div>

          <Button onClick={fetchClaims} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('claims.refresh', 'Refresh')}
          </Button>

          <Button onClick={() => onNavigate?.('billing-claims-bulk')} variant="outline">
            <Table2 className="h-4 w-4 mr-2" />
            {t('claims.bulk_entry', 'Bulk Entry')}
          </Button>

          <Button onClick={() => onNavigate?.('billing-claims-new')}>
            <Plus className="h-4 w-4 mr-2" />
            {t('claims.new_claim', 'New Claim')}
          </Button>
        </div>
      </Card>

      {/* Claims List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p>{t('claims.loading_claims', 'Loading claims...')}</p>
        </div>
      ) : filteredClaims.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">{t('claims.no_claims_found', 'No Claims Found')}</h3>
          <p className="text-gray-600 mb-4">
            {claims.length === 0
              ? 'No claims have been created yet. Click "New Claim" to create your first claim.'
              : 'No claims match your current filters.'}
          </p>
          {claims.length === 0 && (
            <Button onClick={() => onNavigate?.('billing-claims-new')}>
              <Plus className="h-4 w-4 mr-2" />
              {t('claims.create_first_claim', 'Create First Claim')}
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredClaims.map(claim => {
            const isExpanded = expandedClaimId === claim.id;
            const member = members.find(m => m.id === claim.member_id);
            const payer = payers.find(p => p.id === claim.payer_id);

            return (
              <Card key={claim.id} className="overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{claim.claim_number}</h3>
                        {getStatusBadge(claim.claim_status)}
                        {claim.payer_claim_number && (
                          <Badge variant="outline" className="text-xs">
                            ICN: {claim.payer_claim_number}
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span>{member ? `${member.first_name} ${member.last_name}` : `Member ${claim.member_id}`}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          <span>{payer?.payer_name || `Payer ${claim.payer_id}`}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDateOnly(claim.service_date_from)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          <span className="font-semibold">${Number(claim.total_charge || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {claim.claim_status === 'draft' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSubmitClaim(claim.id)}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            {t('claims.submit', 'Submit')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClaim(claim.id, claim.claim_number)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleExpandClaim(claim.id, isExpanded)}
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t pt-4 mt-4 space-y-4">
                      <ClaimReadinessPanel claimId={claim.id} />
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">{t('claims.service_period', 'Service Period:')}</span>
                          <p className="font-medium">
                            {formatDateOnly(claim.service_date_from)} - {formatDateOnly(claim.service_date_to)}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('claims.place_of_service', 'Place of Service:')}</span>
                          <p className="font-medium">{claim.place_of_service || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('claims.billing_provider_npi', 'Billing Provider NPI:')}</span>
                          <p className="font-medium">{claim.billing_provider_npi}</p>
                        </div>
                        {claim.diagnosis_code_1 && (
                          <div>
                            <span className="text-gray-500">{t('claims.primary_diagnosis', 'Primary Diagnosis:')}</span>
                            <p className="font-medium">{claim.diagnosis_code_1}</p>
                          </div>
                        )}
                        {claim.submission_date && (
                          <div>
                            <span className="text-gray-500">{t('claims.submitted', 'Submitted:')}</span>
                            <p className="font-medium">{new Date(claim.submission_date).toLocaleDateString()}</p>
                          </div>
                        )}
                        {claim.payment_date && (
                          <div>
                            <span className="text-gray-500">{t('claims.paid', 'Paid:')}</span>
                            <p className="font-medium">${Number(claim.total_paid || 0).toFixed(2)} on {new Date(claim.payment_date).toLocaleDateString()}</p>
                          </div>
                        )}
                      </div>

                      {claim.internal_notes && (
                        <div>
                          <span className="text-gray-500 text-sm">{t('claims.internal_notes', 'Internal Notes:')}</span>
                          <p className="text-sm mt-1 bg-gray-50 p-2 rounded">{claim.internal_notes}</p>
                        </div>
                      )}

                      {expandedClaimDetail?.line_items && expandedClaimDetail.line_items.length > 0 ? (
                        <div>
                          <h4 className="font-semibold mb-2">{t('claims.line_items', 'Line Items')}</h4>
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left p-2">{t('claims.line', 'Line')}</th>
                                <th className="text-left p-2">CPT/HCPCS</th>
                                <th className="text-left p-2">{t('claims.date', 'Date')}</th>
                                <th className="text-right p-2">{t('claims.units', 'Units')}</th>
                                <th className="text-right p-2">{t('claims.charge', 'Charge')}</th>
                                <th className="text-right p-2">{t('claims.paid_1', 'Paid')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandedClaimDetail.line_items.map((item: any) => (
                                <tr key={item.id} className="border-b">
                                  <td className="p-2">{item.line_number}</td>
                                  <td className="p-2">{item.cpt_code || item.hcpcs_code || 'N/A'}</td>
                                  <td className="p-2">{formatDateOnly(item.service_date)}</td>
                                  <td className="text-right p-2">{item.units}</td>
                                  <td className="text-right p-2">${Number(item.charge_amount || 0).toFixed(2)}</td>
                                  <td className="text-right p-2">${Number(item.paid_amount || 0).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : expandedClaimDetail === null && expandedClaimId === claim.id ? (
                        <div className="text-sm text-gray-500">Loading line items...</div>
                      ) : null}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
